/**
 * Team Phoenix – Portal-Tour (öffentliche Landing/Feature-Seite)
 *
 * • Öffentlich unter `/tour` (kein Login, kein Coming-Soon-Gate).
 * • Nur Deutsch. Marketing-orientiert, dark-emerald „Phoenix"-Look.
 * • Keine Signup-CTAs – reine Info-/Feature-Präsentation.
 */
import { useEffect, useMemo, useRef, useState } from "react";
import {
  LineChart, Line, ResponsiveContainer, XAxis, YAxis, Tooltip, ReferenceLine,
} from "recharts";
import {
  Activity, CalendarDays, MessageSquare, BookOpen, PlayCircle, Newspaper,
  LifeBuoy, Users, Smartphone, Sparkles, Shield, Zap, Globe2, ArrowRight, Check,
} from "lucide-react";

/* ----------------------------------------------------------------- helpers */
function useReveal() {
  const [visible, setVisible] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    if (!ref.current) return;
    const io = new IntersectionObserver(
      ([e]) => e.isIntersecting && setVisible(true),
      { threshold: 0.12 },
    );
    io.observe(ref.current);
    return () => io.disconnect();
  }, []);
  return [ref, visible];
}

function Reveal({ children, delay = 0, className = "" }) {
  const [ref, visible] = useReveal();
  return (
    <div
      ref={ref}
      style={{ transitionDelay: `${delay}ms` }}
      className={`transition-all duration-700 ease-out will-change-transform ${
        visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"
      } ${className}`}
    >
      {children}
    </div>
  );
}

/* ----------------------------------------------------------------- data */
const equityDemo = Array.from({ length: 60 }, (_, i) => {
  const t = i / 59;
  const base = 10000 + 3800 * Math.log(1 + 6 * t);
  const noise = Math.sin(i / 3.4) * 180 + Math.cos(i / 1.7) * 90;
  return { x: i, y: Math.round(base + noise) };
});

const CORE = [
  {
    id: "chart",
    icon: Activity,
    title: "Live Equity-Chart",
    tagline: "Trading-Performance in Echtzeit",
    body:
      "Direkt aus dem MyFXBook-Live-Konto synchronisiert. Tägliche, wöchentliche und monatliche Gewinne im Blick — verifizierbar, transparent, ohne Excel-Bastelei.",
    perks: ["Echtzeit-Sync", "Monats- & Jahresdrilldown", "Öffentlich teilbare Reise-URL"],
  },
  {
    id: "calendar",
    icon: CalendarDays,
    title: "Team-Kalender",
    tagline: "Alle Termine an einem Ort",
    body:
      "Team-Zoom-Calls, Analyse-Sessions, Live-Q&A, Community-Events. Verknüpft mit Google Calendar (3× am Tag automatischer Sync) — nichts geht mehr unter.",
    perks: ["Wiederkehrende Termine", "Zoom-Links & Kategorien", "Sichtbar pro Team"],
  },
  {
    id: "messages",
    icon: MessageSquare,
    title: "Nachrichten",
    tagline: "1:1-Chat und Broadcast",
    body:
      "Direkter Draht zum Mentor, zur Downline oder zum ganzen Team. Broadcasts für Ankündigungen, private Chats mit Push-Benachrichtigung – keine WhatsApp-Chaos-Gruppen mehr.",
    perks: ["Ungelesen-Zähler", "Broadcast an ganzes Team", "Nachrichten löschen möglich"],
  },
  {
    id: "academy",
    icon: BookOpen,
    title: "Academy + KI-Q&A",
    tagline: "Wissensdatenbank auf Steroiden",
    body:
      "Alle Lektionen sauber kategorisiert. Wenn du etwas nicht findest, fragst du unseren KI-Assistenten — er antwortet auf Basis unserer Inhalte in deiner Sprache.",
    perks: ["Über 19 Sprachen", "KI-Antworten mit Quellen", "Fortschritts-Tracking"],
  },
  {
    id: "courses",
    icon: PlayCircle,
    title: "Video-Kurse",
    tagline: "Strukturierter Lernpfad",
    body:
      "Vom Einsteiger-Kurs bis zum fortgeschrittenen Trading-Psychology-Modul. Frage-Antwort pro Lektion — die Community & das Mentoren-Team helfen live.",
    perks: ["iOS-optimiertes Video", "Lernfortschritt", "Q&A pro Lektion"],
  },
  {
    id: "news",
    icon: Newspaper,
    title: "News & Updates",
    tagline: "Wichtige Infos zuerst",
    body:
      "Ankündigungen, Strategie-Updates, Community-News. Pin-Funktion für besonders wichtige Nachrichten. Automatische KI-Übersetzung in 19 Sprachen — jedes Mitglied liest in seiner Sprache.",
    perks: ["Pin & Kategorien", "Bilder & Markdown", "Automatische Übersetzung"],
  },
  {
    id: "support",
    icon: LifeBuoy,
    title: "Support-Tickets",
    tagline: "Hilfe, wenn du sie brauchst",
    body:
      "SLA-getriebenes Ticket-System mit KI-Vorschlägen aus der Wissensdatenbank, bevor du eine Frage stellst. Bewerte die Antwort und trage zur Qualität bei.",
    perks: ["48h-SLA-Timer", "KI-Antwortvorschläge", "CSAT-Bewertung"],
  },
  {
    id: "network",
    icon: Users,
    title: "Mein Team (Netzwerk)",
    tagline: "Deine Downline sichtbar",
    body:
      "Sponsor-/Downline-Struktur, Einladungslinks, Mitglieder-Statistiken. Du siehst dein Team wachsen — sauber, transparent, ohne Fremdanbieter.",
    perks: ["Persönliche Einladungslinks", "Downline-Statistik", "Team-Branding"],
  },
  {
    id: "mobile",
    icon: Smartphone,
    title: "Mobile App",
    tagline: "iOS & Android",
    body:
      "Die native App zeigt deine Reise, Team-News, Termine und den Chat. Push-Benachrichtigungen bei neuen Nachrichten, Terminen und Ankündigungen — inklusive CopyX Partner-Link zum Teilen.",
    perks: ["Push-Benachrichtigungen", "CopyX-Link mit einem Tap", "Offline-freundlich"],
  },
];

/* ----------------------------------------------------------------- tiles */
function FeatureTile({ icon: Icon, title, tagline, body, perks, i }) {
  const off = (i % 3) - 1;
  return (
    <Reveal delay={i * 60} className={`h-full ${off === 0 ? "" : off < 0 ? "lg:-translate-y-3" : "lg:translate-y-3"}`}>
      <article className="group h-full relative rounded-3xl border border-white/10 bg-emerald-900/25 backdrop-blur-xl p-6 sm:p-7 overflow-hidden hover:border-emerald-400/50 transition-colors">
        <div className="pointer-events-none absolute -top-24 -right-24 h-56 w-56 rounded-full bg-emerald-400/10 blur-3xl opacity-0 group-hover:opacity-100 transition-opacity" />
        <div className="flex items-start gap-3">
          <div className="h-10 w-10 rounded-2xl bg-emerald-400/15 border border-emerald-400/30 grid place-items-center shrink-0">
            <Icon className="h-5 w-5 text-emerald-300" strokeWidth={1.8} />
          </div>
          <div className="min-w-0">
            <div className="text-[10px] uppercase tracking-[0.22em] text-emerald-300/70 font-semibold">
              {tagline}
            </div>
            <h3 className="mt-0.5 font-heading text-xl font-bold text-white leading-tight">
              {title}
            </h3>
          </div>
        </div>
        <p className="mt-4 text-sm leading-relaxed text-white/80">
          {body}
        </p>
        <ul className="mt-5 space-y-2">
          {perks.map((p) => (
            <li key={p} className="flex items-start gap-2 text-[13px] text-white/70">
              <Check className="h-3.5 w-3.5 text-emerald-300 mt-1 shrink-0" />
              <span>{p}</span>
            </li>
          ))}
        </ul>
      </article>
    </Reveal>
  );
}

/* ----------------------------------------------------------------- main */
export default function Tour() {
  const chartData = useMemo(() => equityDemo, []);

  return (
    <div data-testid="tour-page" className="min-h-screen bg-[#04120b] text-white overflow-x-hidden">
      {/* Ambient background */}
      <div className="pointer-events-none fixed inset-0 -z-10">
        <div className="absolute inset-0 bg-[radial-gradient(1200px_600px_at_20%_-10%,rgba(34,197,94,0.18),transparent_60%),radial-gradient(900px_500px_at_85%_10%,rgba(20,166,86,0.14),transparent_60%),linear-gradient(180deg,#04120b_0%,#020a06_100%)]" />
        <div className="absolute inset-0 opacity-[0.05] mix-blend-overlay" style={{ backgroundImage: "url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='140' height='140'><filter id='n'><feTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='2'/></filter><rect width='100%' height='100%' filter='url(%23n)'/></svg>\")" }} />
      </div>

      {/* ============================================================ NAV */}
      <header className="sticky top-0 z-30 backdrop-blur-md bg-[#04120b]/60 border-b border-white/5">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 h-16 flex items-center justify-between">
          <a href="/" className="flex items-center gap-2.5 group" data-testid="tour-nav-home">
            <span className="h-7 w-7 rounded-lg bg-emerald-400/20 border border-emerald-400/40 grid place-items-center text-emerald-300 font-black text-sm group-hover:scale-105 transition-transform">TP</span>
            <span className="font-heading font-black tracking-tight">Team Phoenix</span>
          </a>
          <a
            href="/"
            data-testid="tour-nav-login"
            className="inline-flex items-center gap-1.5 rounded-full border border-emerald-400/40 bg-emerald-400/10 text-emerald-100 px-4 py-1.5 text-xs font-semibold hover:bg-emerald-400/20 transition-colors"
          >
            Zum Portal <ArrowRight className="h-3.5 w-3.5" />
          </a>
        </div>
      </header>

      {/* ============================================================ HERO */}
      <section className="max-w-7xl mx-auto px-5 sm:px-8 pt-16 sm:pt-24 pb-20 grid lg:grid-cols-[1.15fr_1fr] gap-14 items-center">
        <div>
          <Reveal>
            <span className="inline-flex items-center gap-1.5 rounded-full border border-emerald-400/30 bg-emerald-400/10 px-3 py-1 text-[11px] uppercase tracking-widest text-emerald-200/90 font-semibold">
              <Sparkles className="h-3 w-3" /> Members-Portal · Copy-Trading
            </span>
          </Reveal>
          <Reveal delay={80}>
            <h1 className="mt-5 font-heading text-4xl sm:text-5xl lg:text-6xl font-black leading-[1.05] tracking-tight">
              Ein Ort für <span className="text-emerald-300">alles</span>,<br />
              was dein Trading-Weg braucht.
            </h1>
          </Reveal>
          <Reveal delay={160}>
            <p className="mt-6 max-w-xl text-base sm:text-lg text-white/75 leading-relaxed">
              Live-Performance, Kalender, KI-Wissensdatenbank, Video-Kurse, Nachrichten,
              Support und dein Team – alles in einem geschützten, mitglieder&shy;exklusiven Bereich.
              Mit nativer Mobile App für iOS und Android.
            </p>
          </Reveal>
          <Reveal delay={240}>
            <div className="mt-8 flex flex-wrap items-center gap-6 text-xs text-white/60">
              <span className="inline-flex items-center gap-1.5"><Shield className="h-3.5 w-3.5 text-emerald-300" /> Nur mit Einladung</span>
              <span className="inline-flex items-center gap-1.5"><Globe2 className="h-3.5 w-3.5 text-emerald-300" /> 19 Sprachen</span>
              <span className="inline-flex items-center gap-1.5"><Zap className="h-3.5 w-3.5 text-emerald-300" /> Live-Sync via MyFXBook</span>
            </div>
          </Reveal>
        </div>

        {/* Hero chart card */}
        <Reveal delay={220}>
          <div className="relative rounded-3xl border border-white/10 bg-gradient-to-br from-emerald-900/40 via-emerald-950/40 to-black/60 backdrop-blur-xl p-5 sm:p-7 shadow-[0_30px_120px_-40px_rgba(16,185,129,0.35)]">
            <div className="flex items-center justify-between">
              <div>
                <div className="text-[10px] uppercase tracking-widest text-emerald-300/80 font-semibold">
                  Live Equity · Preview
                </div>
                <div className="mt-1 font-heading text-2xl font-black">
                  +37,4% <span className="text-emerald-300 text-sm font-semibold">YTD</span>
                </div>
              </div>
              <div className="inline-flex items-center gap-1.5 rounded-full bg-emerald-400/15 border border-emerald-400/30 px-2.5 py-1 text-[10px] uppercase tracking-wider text-emerald-200 font-semibold">
                <span className="h-1.5 w-1.5 rounded-full bg-emerald-300 animate-pulse" /> Live
              </div>
            </div>
            <div className="mt-5 h-52">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={chartData} margin={{ top: 4, right: 0, left: 0, bottom: 0 }}>
                  <defs>
                    <linearGradient id="tourEq" x1="0" y1="0" x2="1" y2="0">
                      <stop offset="0%" stopColor="#34d399" />
                      <stop offset="100%" stopColor="#a7f3d0" />
                    </linearGradient>
                  </defs>
                  <XAxis dataKey="x" hide />
                  <YAxis hide domain={["auto", "auto"]} />
                  <ReferenceLine y={10000} stroke="rgba(255,255,255,0.15)" strokeDasharray="3 3" />
                  <Tooltip
                    cursor={{ stroke: "rgba(52,211,153,0.4)" }}
                    contentStyle={{ background: "rgba(0,20,10,0.85)", border: "1px solid rgba(52,211,153,0.4)", borderRadius: 12, color: "#fff", fontSize: 12 }}
                    labelFormatter={() => ""}
                    formatter={(v) => [`$${Number(v).toLocaleString("de-DE")}`, "Equity"]}
                  />
                  <Line type="monotone" dataKey="y" stroke="url(#tourEq)" strokeWidth={2.5} dot={false} isAnimationActive={true} animationDuration={1400} />
                </LineChart>
              </ResponsiveContainer>
            </div>
            <div className="mt-4 grid grid-cols-3 gap-3 text-center">
              {[
                { l: "Tages-Ø", v: "+0,42%" },
                { l: "Woche", v: "+3,1%" },
                { l: "Monat", v: "+9,7%" },
              ].map((s) => (
                <div key={s.l} className="rounded-xl bg-white/[0.03] border border-white/10 py-2.5">
                  <div className="text-[10px] uppercase tracking-widest text-white/50 font-semibold">{s.l}</div>
                  <div className="text-sm font-bold text-emerald-200">{s.v}</div>
                </div>
              ))}
            </div>
          </div>
        </Reveal>
      </section>

      {/* ============================================================ WERTE */}
      <section className="max-w-7xl mx-auto px-5 sm:px-8 py-16 sm:py-20 border-t border-white/5">
        <Reveal>
          <div className="grid sm:grid-cols-3 gap-6 sm:gap-10">
            {[
              { icon: Shield, t: "Geschützter Rahmen", d: "Kein öffentlicher Chat, keine Fake-Accounts. Zutritt nur mit persönlichem Einladungslink." },
              { icon: Zap, t: "Alles verbunden", d: "Chart, Kurse, Termine, Chat und Team-Struktur greifen ineinander – ohne 5 verschiedene Apps." },
              { icon: Sparkles, t: "KI wo sinnvoll", d: "Wissensdatenbank-Suche und Übersetzungen laufen mit modernster KI – im Hintergrund, ohne Ablenkung." },
            ].map((v, i) => (
              <div key={v.t} className="rounded-2xl bg-white/[0.03] border border-white/10 p-6" style={{ transitionDelay: `${i * 80}ms` }}>
                <div className="h-9 w-9 rounded-xl bg-emerald-400/15 border border-emerald-400/30 grid place-items-center">
                  <v.icon className="h-4.5 w-4.5 text-emerald-300" />
                </div>
                <div className="mt-3 font-heading text-lg font-bold">{v.t}</div>
                <p className="mt-1.5 text-sm text-white/70 leading-relaxed">{v.d}</p>
              </div>
            ))}
          </div>
        </Reveal>
      </section>

      {/* ============================================================ CORE FEATURES */}
      <section className="max-w-7xl mx-auto px-5 sm:px-8 py-8 sm:py-16">
        <Reveal>
          <div className="max-w-2xl">
            <span className="text-[10px] uppercase tracking-[0.22em] text-emerald-300/70 font-semibold">Funktionen</span>
            <h2 className="mt-3 font-heading text-3xl sm:text-4xl font-black leading-tight">
              Das steckt drin – von Chart bis Chat.
            </h2>
            <p className="mt-4 text-white/70 text-base sm:text-lg leading-relaxed">
              Jede Funktion ist dafür gebaut, dass du und dein Team schneller lernen,
              transparenter kommunizieren und selbstständiger handeln könnt.
            </p>
          </div>
        </Reveal>

        <div className="mt-14 grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {CORE.map((f, i) => (
            <FeatureTile key={f.id} {...f} i={i} />
          ))}
        </div>
      </section>

      {/* ============================================================ MOBILE STRIP */}
      <section className="max-w-7xl mx-auto px-5 sm:px-8 py-20">
        <Reveal>
          <div className="relative overflow-hidden rounded-3xl border border-emerald-400/25 bg-gradient-to-br from-emerald-900/50 via-emerald-950/40 to-black/60 p-8 sm:p-14 grid lg:grid-cols-[1fr_1fr] items-center gap-10 backdrop-blur-xl">
            <div>
              <span className="inline-flex items-center gap-1.5 rounded-full border border-emerald-400/30 bg-emerald-400/10 px-3 py-1 text-[11px] uppercase tracking-widest text-emerald-200 font-semibold">
                <Smartphone className="h-3 w-3" /> Neu: Native App
              </span>
              <h3 className="mt-4 font-heading text-3xl sm:text-4xl font-black leading-tight">
                Dein Portal in der Hosentasche.
              </h3>
              <p className="mt-4 text-white/75 leading-relaxed max-w-lg">
                iOS und Android. Push-Benachrichtigungen bei neuen Nachrichten,
                Terminen und Ankündigungen. Direkter Tap-to-Share für deinen
                CopyX Partner-Link – ideal für Gespräche unterwegs.
              </p>
              <ul className="mt-6 space-y-2 text-sm text-white/80">
                {[
                  "Login mit denselben Zugangsdaten wie im Web",
                  "Reise-Fortschritt, Kalender und Nachrichten in einer App",
                  "Videos mit Range-Streaming – kein langes Puffern mehr",
                ].map((t) => (
                  <li key={t} className="flex items-start gap-2">
                    <Check className="h-4 w-4 text-emerald-300 mt-0.5 shrink-0" /> {t}
                  </li>
                ))}
              </ul>
            </div>
            {/* faux phone */}
            <div className="mx-auto max-w-[280px] w-full relative">
              <div className="absolute inset-0 -m-6 bg-emerald-400/20 blur-3xl rounded-full" />
              <div className="relative rounded-[2.4rem] border border-white/15 bg-[#020a06] p-2 shadow-[0_30px_80px_-20px_rgba(16,185,129,0.35)]">
                <div className="rounded-[1.9rem] overflow-hidden aspect-[9/19.5] bg-gradient-to-b from-emerald-950/50 to-black grid place-items-center">
                  <div className="text-center px-6">
                    <span className="h-12 w-12 rounded-2xl bg-emerald-400/20 border border-emerald-400/40 grid place-items-center mx-auto text-emerald-300 font-black text-lg">TP</span>
                    <div className="mt-4 font-heading text-lg font-bold">Team Phoenix</div>
                    <div className="text-[11px] uppercase tracking-widest text-emerald-300/80 font-semibold mt-1">Mobile</div>
                    <div className="mt-8 space-y-2 text-left">
                      {[
                        { l: "Meine Reise", v: "+37,4%" },
                        { l: "Ungelesen", v: "3" },
                        { l: "Nächster Termin", v: "Fr · 20:00" },
                      ].map((k) => (
                        <div key={k.l} className="rounded-lg border border-white/10 bg-white/[0.03] px-3 py-2 flex items-center justify-between">
                          <span className="text-[11px] text-white/60">{k.l}</span>
                          <span className="text-[13px] font-semibold text-emerald-200">{k.v}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </Reveal>
      </section>

      {/* ============================================================ CLOSING */}
      <section className="max-w-4xl mx-auto px-5 sm:px-8 py-20 text-center">
        <Reveal>
          <h3 className="font-heading text-3xl sm:text-4xl font-black leading-tight">
            Ein Portal. Ein Team. Ein Ziel.
          </h3>
          <p className="mt-5 text-white/70 text-base sm:text-lg max-w-2xl mx-auto leading-relaxed">
            Diese Seite ist eine kurze Vorschau. Die Vollversion steht unseren
            Mitgliedern zur Verfügung – mit persönlicher Betreuung, Live-Trades
            und einer stetig wachsenden Community.
          </p>
          <div className="mt-8 inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/[0.03] px-4 py-2 text-xs text-white/70">
            <Shield className="h-3.5 w-3.5 text-emerald-300" /> Zugang ausschließlich über persönliche Einladung
          </div>
        </Reveal>
      </section>

      {/* ============================================================ FOOTER */}
      <footer className="border-t border-white/5 py-8 px-5 sm:px-8">
        <div className="max-w-7xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-3 text-[11px] text-white/40">
          <div>© {new Date().getFullYear()} Team Phoenix. Alle Rechte vorbehalten.</div>
          <div>Portal-Tour · nur zu Informationszwecken.</div>
        </div>
      </footer>
    </div>
  );
}
